// Turn a cursor-based backend into a forward/back page source (ADR-0010 plan §3,
// narrowed per review §8).
//
// The CardCollection shell speaks in page numbers, but the search backend is
// cursor-based. A cursor backend can only move ONE step from a known cursor, so
// the pager's public surface is deliberately minimal: current / next / prev /
// reset. It caches each visited page's (items, total) and the cursor that yields
// the NEXT page, so:
//   - next() is one backend call per new page (or a cache hit when revisited),
//   - prev() is always a cache hit (already visited),
//   - reset() drops the cache for a fresh query/filter/sort.
//
// There is no arbitrary-page jump: addressing page N on a cursor backend can
// only be done by walking N pages, which is what next() already does one step at
// a time. The caller (SearchPage) steps toward the shell's requested page.
//
// `fetchPage(cursor)` must return { items, total, next_cursor } (next_cursor null
// at the end). It is the only backend coupling; everything else is pure walking.

/**
 * @param {(cursor: string|null) => Promise<{items: any[], total: number, next_cursor: string|null}>} fetchPage
 */
export function createCursorPager(fetchPage) {
  // pageCache[n] = { items, total }; nextCursor[n] = cursor that yields page n+1.
  let pageCache = new Map();
  let nextCursor = new Map(); // n → cursor for page n+1 (page 1's cursor is null)
  let total = 0;
  let current = 0; // 0 = nothing fetched yet; first next() lands on page 1

  async function fetchPage_(n, cursor) {
    if (pageCache.has(n)) return pageCache.get(n);
    const res = await fetchPage(cursor);
    total = res?.total ?? total;
    const result = { items: res?.items || [], total };
    pageCache.set(n, result);
    if (res?.next_cursor != null) nextCursor.set(n, res.next_cursor);
    return result;
  }

  return {
    /** The current page number (1-based; 0 before the first fetch). */
    currentPage() {
      return current;
    },
    /** Whether a next page is known to exist (cursor or cache present). */
    hasNext() {
      return current === 0 || nextCursor.has(current) || pageCache.has(current + 1);
    },
    /** (Re)fetch the current page; fetches page 1 on first call. */
    async current_() {
      if (current === 0) return this.next();
      return fetchPage_(current, current === 1 ? null : nextCursor.get(current - 1));
    },
    /** Advance one page and return it (no-op past the end → empty result). */
    async next() {
      if (current === 0) {
        current = 1;
        return fetchPage_(1, null);
      }
      if (!this.hasNext()) {
        current += 1;
        const empty = { items: [], total };
        pageCache.set(current, empty);
        return empty;
      }
      const cursor = nextCursor.get(current);
      current += 1;
      return fetchPage_(current, cursor);
    },
    /** Step back one page and return it (always a cache hit; clamps at 1). */
    async prev() {
      if (current > 1) current -= 1;
      return pageCache.get(current) || { items: [], total };
    },
    reset() {
      pageCache = new Map();
      nextCursor = new Map();
      total = 0;
      current = 0;
    },
  };
}
